import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Switch,
  Button,
  Slider,
  Skeleton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import {
  resellingConfigAction,
  updateCheckinConfigAction,
  updateCreditConfigAction,
  getUserProfileAction,
} from "../../../redux/actions";
import { showSnackbar } from "../../../redux/slices/SnackbarSlice";
import { commonSwitchStyles } from "../../../theme/CommonStyles";

const typographyStyle = {
  fontFamily: "var(--font-family-manrope)",
  fontWeight: 400,
  fontSize: "14px",
};

const customSwitchStyle = commonSwitchStyles;

const ResellingPricing: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { configData, configLoading, saveConfigLoading, accountStatusData } =
    useSelector((state: RootState) => state.reselling);

  const { configurationData } = useSelector((state: RootState) => state.helper);

  const dashboardData = useSelector(
    (state: RootState) => state.dashboard.dashboardData,
  );

  // Dynamic pricing fields from Profile API / Helper Config
  const originalCostPerAiCall = useMemo(() => {
    return (
      dashboardData?.configs?.[0]?.originalCostPerAiCall ??
      (Array.isArray(configurationData)
        ? configurationData?.[0]?.originalCostPerAiCall
        : configurationData?.originalCostPerAiCall) ??
      5
    );
  }, [dashboardData?.configs, configurationData]);

  const originalCostPer100AiCredits = useMemo(() => {
    return (
      dashboardData?.configs?.[0]?.originalCostPer100AiCredits ??
      (Array.isArray(configurationData)
        ? configurationData?.[0]?.originalCostPer100AiCredits
        : configurationData?.originalCostPer100AiCredits) ??
      1
    );
  }, [dashboardData?.configs, configurationData]);

  const costPerAiCall = useMemo(() => {
    return (
      dashboardData?.configs?.[0]?.costPerAiCall ??
      (Array.isArray(configurationData)
        ? configurationData?.[0]?.costPerAiCall
        : configurationData?.costPerAiCall) ??
      5
    );
  }, [dashboardData?.configs, configurationData]);

  const costPer100AiCredits = useMemo(() => {
    return (
      dashboardData?.configs?.[0]?.costPer100AiCredits ??
      (Array.isArray(configurationData)
        ? configurationData?.[0]?.costPer100AiCredits
        : configurationData?.costPer100AiCredits) ??
      5
    );
  }, [dashboardData?.configs, configurationData]);

  // Map Check-Ins state
  const [checkinEnabled, setCheckinEnabled] = useState<boolean>(true);
  const [resalePrice, setResalePrice] = useState<number>(299);
  const [trialDays, setTrialDays] = useState<number>(14);
  const [requirePaymentUpfront, setRequirePaymentUpfront] =
    useState<boolean>(true);

  // AI Credits state
  const [creditsEnabled, setCreditsEnabled] = useState<boolean>(true);
  const [markupMultiplier, setMarkupMultiplier] = useState<number>(1.5);
  const [creditsPerAction, setCreditsPerAction] = useState<number | string>(
    originalCostPerAiCall,
  );

  // Fetch initial reselling config on mount
  useEffect(() => {
    dispatch(resellingConfigAction());
  }, [dispatch]);

  // Fetch user profile on mount if not already loaded
  useEffect(() => {
    if (!dashboardData) {
      dispatch(getUserProfileAction());
    }
  }, [dispatch, dashboardData]);

  const isStripeConnected =
    Boolean(accountStatusData?.stripe_account_id) &&
    accountStatusData?.onboarding_status === "active";

  // Tooltip state for switches when Stripe is not connected
  const [checkinTooltipOpen, setCheckinTooltipOpen] = useState(false);
  const [creditsTooltipOpen, setCreditsTooltipOpen] = useState(false);

  const stripeTooltipMsg = "Please connect Stripe first to enable this feature";

  const handleCheckinSwitchChange = (checked: boolean) => {
    if (!isStripeConnected && checked) {
      setCheckinTooltipOpen(true);
      setTimeout(() => setCheckinTooltipOpen(false), 2500);
      return;
    }
    setCheckinEnabled(checked);
  };

  const handleCreditsSwitchChange = (checked: boolean) => {
    if (!isStripeConnected && checked) {
      setCreditsTooltipOpen(true);
      setTimeout(() => setCreditsTooltipOpen(false), 2500);
      return;
    }
    setCreditsEnabled(checked);
  };

  // Body is disabled when Stripe not connected OR when the switch is off
  const isCheckinBodyDisabled = !isStripeConnected || !checkinEnabled;
  const isCreditsBodyDisabled = !isStripeConnected || !creditsEnabled;

  // Sync state when configData updates from backend
  useEffect(() => {
    if (configData) {
      setCheckinEnabled(Boolean(configData.checkin_enabled));
      if (
        configData.resale_price_cents !== undefined &&
        configData.resale_price_cents !== null &&
        configData.resale_price_cents > 0
      ) {
        setResalePrice(configData.resale_price_cents / 100);
      } else {
        setResalePrice(299);
      }
      if (configData.trial_days !== undefined) {
        setTrialDays(configData.trial_days);
      }
      if (configData.require_payment_upfront !== undefined) {
        setRequirePaymentUpfront(Boolean(configData.require_payment_upfront));
      }
      setCreditsEnabled(Boolean(configData.credits_enabled));
      if (
        configData.markup_multiplier !== undefined &&
        configData.markup_multiplier !== null
      ) {
        setMarkupMultiplier(configData.markup_multiplier);
      } else {
        setMarkupMultiplier(1.5);
      }
      if (
        configData.credits_per_action !== undefined &&
        configData.credits_per_action !== null &&
        configData.credits_per_action > 0
      ) {
        setCreditsPerAction(configData.credits_per_action);
      } else {
        setCreditsPerAction(originalCostPerAiCall);
      }
    }
  }, [configData, originalCostPerAiCall]);

  // Wholesale cost dynamically calculated from user profile addon response
  const mapCheckinAddon = useMemo(() => {
    return dashboardData?.addons?.find(
      (item: any) => item.addon_id === "map_checkin",
    );
  }, [dashboardData?.addons]);

  const wholesaleCostCents = mapCheckinAddon?.reseller_cost_cents ?? 9900;
  const wholesaleCost = wholesaleCostCents / 100; // Wholesale cost per location (e.g. $99)

  // Economics Calculations for Map Check-Ins
  const profitPerLocation = Math.max(0, resalePrice - wholesaleCost);
  const marginPercent =
    resalePrice > 0
      ? Math.round(((resalePrice - wholesaleCost) / resalePrice) * 100)
      : 0;
  const activeLocations = configData?.total_active_locations ?? 0;
  const totalRecurringProfit = activeLocations * profitPerLocation;

  // Effective credits per action (guarantees valid number >= originalCostPerAiCall)
  const effectiveCreditsPerAction = useMemo(() => {
    const parsed =
      typeof creditsPerAction === "number"
        ? creditsPerAction
        : parseInt(String(creditsPerAction), 10);
    return !isNaN(parsed) && parsed >= originalCostPerAiCall
      ? parsed
      : originalCostPerAiCall;
  }, [creditsPerAction, originalCostPerAiCall]);

  // Economics Calculations for AI Credits
  const costPerCredit = originalCostPer100AiCredits / 100;
  const costPerAction = originalCostPerAiCall * costPerCredit;

  const clientBaseCostPerCredit =
    (markupMultiplier * originalCostPer100AiCredits) / 100;
  const chargedPerCredit = clientBaseCostPerCredit;
  const chargedPerAction = effectiveCreditsPerAction * chargedPerCredit;
  const yourActionsForTen =
    costPerAction > 0 ? Math.floor(10 / costPerAction) : 0;
  const actionsForTen =
    chargedPerAction > 0 ? Math.floor(10 / chargedPerAction) : 0;

  const profitPerCredit = chargedPerCredit - costPerCredit;
  const profitPerAction = chargedPerAction - costPerAction;
  const profitForTen = actionsForTen * profitPerAction;

  // Handle Trial Preset Click
  const handleTrialSelect = (days: number) => {
    setTrialDays(days);
  };

  // Handle Save Changes
  const handleSaveChanges = async () => {
    try {
      const checkinPayload = {
        checkin_enabled: checkinEnabled,
        resale_price_cents: Math.round(resalePrice * 100),
        trial_days: trialDays,
        require_payment_upfront: requirePaymentUpfront,
      };

      const creditPayload = {
        credits_enabled: creditsEnabled,
        markup_multiplier: markupMultiplier,
        credits_per_action: effectiveCreditsPerAction,
      };

      await Promise.all([
        dispatch(updateCheckinConfigAction(checkinPayload)).unwrap(),
        dispatch(updateCreditConfigAction(creditPayload)).unwrap(),
      ]);

      await dispatch(resellingConfigAction()).unwrap();

      dispatch(
        showSnackbar({
          message: "Reseller pricing configuration saved successfully",
          type: "success",
        }),
      );
    } catch (err: any) {
      console.error("Save reselling pricing failed:", err);
      dispatch(
        showSnackbar({
          message:
            err?.message || "Failed to save reseller pricing configuration",
          type: "error",
        }),
      );
    }
  };

  if (configLoading) {
    return (
      <Box sx={{ maxWidth: "1000px", pb: 5 }}>
        <Skeleton width={300} height={40} sx={{ mb: 2 }} />
        <Skeleton
          variant="rectangular"
          height={240}
          sx={{ borderRadius: 2, mb: 3 }}
        />
        <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1000px",
        pb: 8,
        fontFamily: "var(--font-family-manrope)",
      }}
    >
      {/* SECTION 1: MAP CHECK-INS HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <Box>
          <Typography
            sx={{
              ...typographyStyle,
              fontSize: "24px",
              fontWeight: 500,
              color: "#111827",
            }}
          >
            Map Check-Ins
          </Typography>
          <Typography
            sx={{
              ...typographyStyle,
              color: "#6B7280",
              fontSize: "14px",
              mt: 1,
              maxWidth: "520px",
            }}
          >
            Bill each business location to your client at a flat monthly price.
            You pay{" "}
            <strong style={{ color: "#111827" }}>${wholesaleCost}/mo</strong>{" "}
            per location; the spread is yours.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, pt: 0.5 }}>
          <Typography
            sx={{
              ...typographyStyle,
              fontSize: "14px",
              color: checkinEnabled
                ? "var(--action-button-background-color, #2563EB)"
                : "#6B7280",
              fontWeight: 500,
            }}
          >
            {checkinEnabled ? "Enabled" : "Off"}
          </Typography>
          <Tooltip
            title={stripeTooltipMsg}
            open={checkinTooltipOpen}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#5e5d5dff",
                  border: "1px solid #d8d8d8",
                  fontSize: "12px",
                  borderRadius: "var(--small-border-radius)",
                  fontWeight: 500,
                  p: 1,
                },
              },
              arrow: {
                sx: {
                  color: "#d8d8d8",
                },
              },
            }}
          >
            <Switch
              checked={checkinEnabled}
              onChange={(e) => handleCheckinSwitchChange(e.target.checked)}
              sx={customSwitchStyle}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* MAP CHECK-INS BODY (OPEN GRID ROWS WITH TOP DIVIDERS) */}
      <Box
        sx={{
          mt: 3.5,
          opacity: isCheckinBodyDisabled ? 0.5 : 1,
          pointerEvents: isCheckinBodyDisabled ? "none" : "auto",
          userSelect: isCheckinBodyDisabled ? "none" : "auto",
          transition: "opacity 0.2s ease",
        }}
      >
          {/* Row 1: Resale price */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
              gap: "24px",
              alignItems: "center",
              py: 2.25,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Box>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#111827",
                }}
              >
                Resale price
              </Typography>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontSize: "12px",
                  color: "#6B7280",
                  mt: 0.25,
                }}
              >
                Per location, per month
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "44px",
                  border: "1px solid #D1D5DB",
                  borderRadius: "8px",
                  px: 1.75,
                  width: "180px",
                  backgroundColor: "#FFF",
                  "&:focus-within": {
                    borderColor:
                      "var(--action-button-background-color, #2563EB)",
                  },
                }}
              >
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontSize: "16px",
                    color: "#6B7280",
                    mr: 0.5,
                  }}
                >
                  $
                </Typography>
                <input
                  type="number"
                  min={wholesaleCost}
                  value={resalePrice}
                  onChange={(e) => setResalePrice(Number(e.target.value))}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#111827",
                    width: "100%",
                    fontFamily: "var(--font-family-manrope)",
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                {[149, 199, 299].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setResalePrice(preset)}
                    style={{
                      height: "44px",
                      padding: "0 16px",
                      borderRadius: "8px",
                      border:
                        resalePrice === preset
                          ? "1px solid var(--action-button-background-color, #2563EB)"
                          : "1px solid #D1D5DB",
                      backgroundColor:
                        resalePrice === preset ? "#EFF6FF" : "#FFFFFF",
                      color:
                        resalePrice === preset
                          ? "var(--action-button-background-color, #2563EB)"
                          : "#374151",
                      fontSize: "14px",
                      fontFamily: "var(--font-family-manrope)",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    ${preset}
                  </button>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Row 2: Your economics */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
              gap: "24px",
              alignItems: "center",
              py: 2.25,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Typography
              sx={{
                ...typographyStyle,
                fontWeight: 600,
                fontSize: "14px",
                color: "#111827",
              }}
            >
              Your economics
            </Typography>
            <Typography
              sx={{ ...typographyStyle, fontSize: "14px", color: "#374151" }}
            >
              You pay{" "}
              <strong style={{ color: "#111827" }}>${wholesaleCost}</strong>{" "}
              &nbsp;·&nbsp; client pays{" "}
              <strong
                style={{
                  color: "var(--action-button-background-color, #2563EB)",
                }}
              >
                ${resalePrice.toFixed(2)}
              </strong>{" "}
              &nbsp;·&nbsp; you keep{" "}
              <strong style={{ color: "#16A34A" }}>
                ${profitPerLocation.toFixed(2)}
              </strong>{" "}
              <span style={{ color: "#6B7280" }}>({marginPercent}%)</span>
              <Typography
                component="div"
                sx={{
                  ...typographyStyle,
                  fontSize: "12px",
                  color: "#6B7280",
                  mt: 0.75,
                }}
              >
                Across your {activeLocations} active locations →{" "}
                <strong style={{ color: "#16A34A" }}>
                  ${totalRecurringProfit.toLocaleString()}/mo
                </strong>{" "}
                recurring.
              </Typography>
            </Typography>
          </Box>

          {/* Row 3: Free trial */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
              gap: "24px",
              alignItems: "center",
              py: 2.25,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Box>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#111827",
                }}
              >
                Free trial
              </Typography>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontSize: "12px",
                  color: "#6B7280",
                  mt: 0.25,
                }}
              >
                Free days before the first charge
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => handleTrialSelect(0)}
                style={{
                  height: "44px",
                  padding: "0 16px",
                  borderRadius: "8px",
                  border:
                    trialDays === 0
                      ? "1px solid var(--action-button-background-color, #2563EB)"
                      : "1px solid #D1D5DB",
                  backgroundColor: trialDays === 0 ? "#EFF6FF" : "#FFFFFF",
                  color:
                    trialDays === 0
                      ? "var(--action-button-background-color, #2563EB)"
                      : "#374151",
                  fontSize: "14px",
                  fontFamily: "var(--font-family-manrope)",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                No trial
              </button>

              {[7, 14].map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() => handleTrialSelect(days)}
                  style={{
                    height: "44px",
                    padding: "0 16px",
                    borderRadius: "10px",
                    border:
                      trialDays === days
                        ? "1.5px solid var(--action-button-background-color, #2563EB)"
                        : "1px solid #D1D5DB",
                    backgroundColor: trialDays === days ? "#EFF6FF" : "#FFFFFF",
                    color:
                      trialDays === days
                        ? "var(--action-button-background-color, #2563EB)"
                        : "#374151",
                    fontSize: "14px",
                    fontFamily: "var(--font-family-manrope)",
                    fontWeight: trialDays === days ? 600 : 500,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {days} days
                </button>
              ))}
            </Box>
          </Box>

          {/* Row 4: Collect payment upfront */}
          {trialDays > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
                gap: "24px",
                alignItems: "center",
                py: 2.25,
                borderTop: "1px solid #E5E7EB",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#111827",
                  }}
                >
                  Collect payment upfront
                </Typography>
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontSize: "12px",
                    color: "#6B7280",
                    mt: 0.25,
                  }}
                >
                  Require a card to start the trial
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Switch
                  checked={requirePaymentUpfront}
                  onChange={(e) => setRequirePaymentUpfront(e.target.checked)}
                  sx={customSwitchStyle}
                />
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontSize: "12px",
                    color: "#6B7280",
                  }}
                >
                  Higher conversion and less trial abuse. Recommended.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Row 5: What the client sees */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
              gap: "24px",
              alignItems: "center",
              py: 2.25,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Typography
              sx={{
                ...typographyStyle,
                fontWeight: 600,
                fontSize: "14px",
                color: "#111827",
              }}
            >
              What the client sees
            </Typography>

            <Box
              sx={{
                p: 2,
                px: 2.5,
                backgroundColor: "#EFF6FF",
                border: "1px solid #BFDBFE",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <InfoOutlinedIcon
                sx={{ color: "#2563EB", fontSize: "20px", flexShrink: 0 }}
              />
              <Typography
                sx={{
                  ...typographyStyle,
                  fontSize: "14px",
                  color: "#1E40AF",
                  lineHeight: 1.5,
                }}
              >
                {trialDays > 0 ? (
                  <>
                    New clients{" "}
                    {requirePaymentUpfront
                      ? "add a card up front, get "
                      : "get "}
                    <strong>{trialDays} days free</strong>, then are billed{" "}
                    <strong>${resalePrice}/mo per location</strong>{" "}
                    automatically when the trial ends.
                  </>
                ) : (
                  <>
                    New clients pay{" "}
                    <strong>${resalePrice}/mo per location</strong> immediately
                    upon sign-up.
                  </>
                )}
              </Typography>
            </Box>
          </Box>
      </Box>

      {/* SECTION 2: AI CREDITS HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "24px",
          mt: 7,
        }}
      >
        <Box>
          <Typography
            sx={{
              ...typographyStyle,
              fontSize: "24px",
              fontWeight: 500,
              color: "#111827",
            }}
          >
            AI Credits
          </Typography>
          <Typography
            sx={{
              ...typographyStyle,
              color: "#6B7280",
              fontSize: "14px",
              mt: 1,
              maxWidth: "520px",
            }}
          >
            Clients top up their own AI credits through your Stripe. Two levers:
            mark up the price, and set how many credits each AI action costs.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, pt: 0.5 }}>
          <Typography
            sx={{
              ...typographyStyle,
              fontSize: "14px",
              color: creditsEnabled
                ? "var(--action-button-background-color, #2563EB)"
                : "#6B7280",
              fontWeight: 500,
            }}
          >
            {creditsEnabled ? "Enabled" : "Off"}
          </Typography>
          <Tooltip
            title={stripeTooltipMsg}
            open={creditsTooltipOpen}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#5e5d5dff",
                  border: "1px solid #d8d8d8",
                  fontSize: "12px",
                  borderRadius: "var(--small-border-radius)",
                  fontWeight: 500,
                  p: 1,
                },
              },
              arrow: {
                sx: {
                  color: "#d8d8d8",
                },
              },
            }}
          >
            <Switch
              checked={creditsEnabled}
              onChange={(e) => handleCreditsSwitchChange(e.target.checked)}
              sx={customSwitchStyle}
            />
          </Tooltip>
        </Box>
      </Box>

      {/* AI CREDITS BODY (OPEN GRID ROWS WITH TOP DIVIDERS) */}
      <Box
        sx={{
          mt: 3.5,
          opacity: isCreditsBodyDisabled ? 0.5 : 1,
          pointerEvents: isCreditsBodyDisabled ? "none" : "auto",
          userSelect: isCreditsBodyDisabled ? "none" : "auto",
          transition: "opacity 0.2s ease",
        }}
      >
          {/* Row 1: Price markup */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
              gap: "24px",
              alignItems: "center",
              py: 2.5,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Box>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#111827",
                }}
              >
                Price markup
              </Typography>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontSize: "12px",
                  color: "#6B7280",
                  mt: 0.25,
                }}
              >
                On our ${originalCostPer100AiCredits} / 100 credits
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2.5,
                maxWidth: "460px",
              }}
            >
              <Slider
                value={markupMultiplier}
                min={1.0}
                max={5.0}
                step={0.05}
                onChange={(_, val) => setMarkupMultiplier(val as number)}
                sx={{
                  color: "var(--action-button-background-color, #2563EB)",
                  height: 6,
                  "& .MuiSlider-thumb": {
                    width: 18,
                    height: 18,
                    backgroundColor: "#FFF",
                    border:
                      "2px solid var(--action-button-background-color, #2563EB)",
                    "&:focus, &:hover, &.Mui-active": {
                      boxShadow: "inherit",
                    },
                  },
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                  minWidth: "64px",
                  justifyContent: "flex-end",
                }}
              >
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {markupMultiplier.toFixed(2)}
                </Typography>
                <Typography
                  sx={{
                    ...typographyStyle,
                    fontSize: "12px",
                    color: "#6B7280",
                  }}
                >
                  ×
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Row 2: Credits per AI action */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
              gap: "24px",
              alignItems: "center",
              py: 2.5,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Box>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#111827",
                }}
              >
                Credits per AI action
              </Typography>
              <Typography
                sx={{
                  ...typographyStyle,
                  fontSize: "12px",
                  color: "#6B7280",
                  mt: 0.25,
                }}
              >
                Our default is {originalCostPerAiCall}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="number"
                  min={originalCostPerAiCall}
                  max="25"
                  value={creditsPerAction}
                  onChange={(e) => {
                    const valStr = e.target.value;
                    if (valStr === "") {
                      setCreditsPerAction("");
                      return;
                    }
                    const val = parseInt(valStr, 10);
                    if (!isNaN(val)) {
                      setCreditsPerAction(val);
                    }
                  }}
                  onBlur={() => {
                    const parsed =
                      typeof creditsPerAction === "number"
                        ? creditsPerAction
                        : parseInt(String(creditsPerAction), 10);
                    if (isNaN(parsed) || parsed < originalCostPerAiCall) {
                      setCreditsPerAction(originalCostPerAiCall);
                    }
                  }}
                  style={{
                    width: "90px",
                    height: "44px",
                    border: "1px solid #D1D5DB",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "#111827",
                    outline: "none",
                    fontFamily: "var(--font-family-manrope)",
                  }}
                />
                <button
                  type="button"
                  onClick={() =>
                    setCreditsPerAction((prev) => {
                      const num =
                        typeof prev === "number"
                          ? prev
                          : parseInt(String(prev), 10);
                      return !isNaN(num)
                        ? Math.max(originalCostPerAiCall, num - 1)
                        : originalCostPerAiCall;
                    })
                  }
                  disabled={effectiveCreditsPerAction <= originalCostPerAiCall}
                  style={{
                    width: "44px",
                    height: "44px",
                    border: "1px solid #D1D5DB",
                    borderRadius: "8px",
                    color: "#111827",
                    fontSize: "16px",
                    fontFamily: "var(--font-family-manrope)",
                    cursor:
                      effectiveCreditsPerAction <= originalCostPerAiCall
                        ? "not-allowed"
                        : "pointer",
                    backgroundColor: "#F9FAFB",
                  }}
                >
                  −
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setCreditsPerAction((prev) => {
                      const num =
                        typeof prev === "number"
                          ? prev
                          : parseInt(String(prev), 10);
                      return !isNaN(num) ? num + 1 : originalCostPerAiCall + 1;
                    })
                  }
                  style={{
                    width: "44px",
                    height: "44px",
                    border: "1px solid #D1D5DB",
                    borderRadius: "8px",
                    color: "#111827",
                    fontSize: "16px",
                    fontFamily: "var(--font-family-manrope)",
                    cursor: "pointer",
                    backgroundColor: "#F9FAFB",
                  }}
                >
                  +
                </button>
              </Box>

              <Typography
                sx={{
                  ...typographyStyle,
                  fontSize: "12px",
                  color: "#6B7280",
                  maxWidth: "280px",
                }}
              >
                Floor is {originalCostPerAiCall} (our cost). Raising it drains
                balances faster — keep it fair to clients.
              </Typography>
            </Box>
          </Box>

          {/* Row 3: What your client pays */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "260px 1fr" },
              gap: "24px",
              alignItems: "flex-start",
              pt: 3,
              pb: 0.5,
              borderTop: "1px solid #E5E7EB",
            }}
          >
            <Typography
              sx={{
                ...typographyStyle,
                fontWeight: 600,
                fontSize: "14px",
                color: "#111827",
              }}
            >
              What your client pays
            </Typography>

            <Box sx={{ width: "100%", maxWidth: "520px" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                  fontFamily: "var(--font-family-manrope)",
                }}
              >
                <thead>
                  <tr style={{ color: "#6B7280", fontSize: "12px" }}>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0 0 10px",
                        fontWeight: 500,
                      }}
                    ></th>
                    <th
                      style={{
                        padding: "0 0 10px",
                        fontWeight: 500,
                        textAlign: "right",
                      }}
                    >
                      Per credit
                    </th>
                    <th
                      style={{
                        padding: "0 0 10px",
                        fontWeight: 500,
                        textAlign: "right",
                      }}
                    >
                      Per action
                    </th>
                    <th
                      style={{
                        padding: "0 0 10px",
                        fontWeight: 500,
                        textAlign: "right",
                      }}
                    >
                      $10 buys
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row 1: Your cost */}
                  <tr style={{ borderTop: "1px solid #E5E7EB" }}>
                    <td
                      style={{
                        padding: "11px 0",
                        color: "#6B7280",
                        fontSize: "14px",
                      }}
                    >
                      Your cost
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      ${costPerCredit.toFixed(3)}
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      ${costPerAction.toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "#6B7280",
                        fontSize: "14px",
                      }}
                    >
                      {yourActionsForTen} actions
                    </td>
                  </tr>

                  {/* Row 2: Charged to client */}
                  <tr style={{ borderTop: "1px solid #E5E7EB" }}>
                    <td
                      style={{
                        padding: "11px 0",
                        color: "#6B7280",
                        fontSize: "14px",
                      }}
                    >
                      Charged to client
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "var(--action-button-background-color, #2563EB)",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      ${chargedPerCredit.toFixed(3)}
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "var(--action-button-background-color, #2563EB)",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      ${chargedPerAction.toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "#374151",
                        fontSize: "14px",
                      }}
                    >
                      {actionsForTen} actions
                    </td>
                  </tr>

                  {/* Row 3: Your profit */}
                  <tr style={{ borderTop: "1px solid #E5E7EB" }}>
                    <td
                      style={{
                        padding: "11px 0",
                        color: "#16A34A",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      Your profit
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "#16A34A",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      ${profitPerCredit.toFixed(3)}
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "#16A34A",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      ${profitPerAction.toFixed(2)}
                    </td>
                    <td
                      style={{
                        padding: "11px 0",
                        textAlign: "right",
                        color: "#16A34A",
                        fontWeight: 700,
                        fontSize: "14px",
                      }}
                    >
                      ${profitForTen.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </Box>
      </Box>

      {/* SAVE CHANGES BUTTON */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 5,
          pt: 3,
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <Button
          variant="contained"
          disabled={!isStripeConnected || saveConfigLoading}
          onClick={handleSaveChanges}
          sx={{
            backgroundColor: "var(--action-button-background-color)",
            color: "white",
            borderRadius: "var(--action-button-radius)",
            border: "none",
            padding: "var(--buttons-padding)",
            fontSize: "var(--medium-body-font-size)",
            fontFamily: "var(--font-family-manrope)",
            fontWeight: "400  !important",
            textTransform: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
            },
          }}
        >
          {saveConfigLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            "Save changes"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ResellingPricing;
